import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Heart } from "lucide-react";

export default function Auth() {
    const { login, register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const initialRole = params.get("role") || "adopter";
    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({ email: "", password: "", name: "", role: initialRole, location: "" });
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            if (mode === "login") {
                await login(form.email, form.password);
                toast.success("Welcome back");
            } else {
                await register(form);
                toast.success("Welcome to RescueLink");
            }
            nav("/");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Authentication failed");
        }
        setBusy(false);
    }

    return (
        <div className="min-h-[70vh] flex items-center justify-center py-14 px-6" data-testid="auth-page">
            <div className="w-full max-w-md card-warm rounded-2xl p-8">
                <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 justify-center">
                        <div className="w-9 h-9 rounded-full bg-[var(--rl-primary)] flex items-center justify-center"><Heart className="w-4 h-4 text-white" fill="white" strokeWidth={0} /></div>
                        <span className="font-serif text-2xl">RescueLink</span>
                    </div>
                    <h1 className="font-serif text-3xl mt-4">{mode === "login" ? "Welcome back" : "Join the network"}</h1>
                    <p className="text-sm text-[var(--rl-text-2)] mt-1">{mode === "login" ? "Sign in to pledge and post." : "Free forever — for adopters, fosters, and rescuers."}</p>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    {mode === "register" && (
                        <>
                            <div>
                                <label className="eyebrow">Name</label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                                       className="mt-1.5 border-[var(--rl-border)] h-11" data-testid="auth-name" />
                            </div>
                            <div>
                                <label className="eyebrow">Role</label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger className="mt-1.5 border-[var(--rl-border)] h-11" data-testid="auth-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="adopter">Adopter</SelectItem>
                                        <SelectItem value="foster">Foster</SelectItem>
                                        <SelectItem value="volunteer">Volunteer</SelectItem>
                                        <SelectItem value="rescue">Rescue organization</SelectItem>
                                        <SelectItem value="shelter">Shelter</SelectItem>
                                        <SelectItem value="partner">Service Partner</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="eyebrow">Location</label>
                                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                                       placeholder="City, State" className="mt-1.5 border-[var(--rl-border)] h-11" data-testid="auth-location" />
                            </div>
                        </>
                    )}
                    <div>
                        <label className="eyebrow">Email</label>
                        <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" required
                               className="mt-1.5 border-[var(--rl-border)] h-11" data-testid="auth-email" />
                    </div>
                    <div>
                        <label className="eyebrow">Password</label>
                        <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} type="password" required
                               className="mt-1.5 border-[var(--rl-border)] h-11" data-testid="auth-password" />
                    </div>
                    <Button type="submit" disabled={busy} className="btn-primary w-full rounded-full h-11" data-testid="auth-submit">
                        {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
                    </Button>
                </form>

                <div className="text-center text-sm mt-6">
                    {mode === "login" ? (
                        <>New here? <button onClick={() => setMode("register")} className="text-[var(--rl-primary)] font-semibold" data-testid="switch-to-register">Create an account</button></>
                    ) : (
                        <>Already have an account? <button onClick={() => setMode("login")} className="text-[var(--rl-primary)] font-semibold" data-testid="switch-to-login">Sign in</button></>
                    )}
                </div>
                {mode === "login" && (
                    <div className="mt-4 text-center text-xs text-[var(--rl-text-3)]">
                        Try the demo: <span className="font-mono">demo@rescuelink.org</span> / <span className="font-mono">demo1234</span>
                    </div>
                )}
                <div className="mt-6 text-center">
                    <Link to="/" className="text-xs text-[var(--rl-text-3)] hover:text-[var(--rl-text-2)]">← Back to RescueLink</Link>
                </div>
            </div>
        </div>
    );
}
